import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  MapPin,
  Phone,
  User,
  MessageSquare,
  CheckCircle2,
  ShoppingBag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useCartStore } from '@/features/cart/stores/cart-store';
import { createOrder } from '@/features/checkout/api/create-order';
import { supabase } from '@/lib/supabase';
import { resolvePricingConfig } from '@/features/settings/pricing-config';
import { useSettings, useShowPrices } from '@/features/settings/hooks/use-settings';
import { resolveDefaultBranchId } from '@/features/checkout/api/resolve-default-branch';
import { MpesaPaymentSection } from '@/features/checkout/components/mpesa-payment-section';
import { renderSmsTemplate, WHATSAPP_ORDER_MESSAGE_DEFAULT } from '@/features/sms/config';
import {
  getContactDisplayNumber,
  getContactWhatsappTarget,
  type SupportContact,
} from '@/features/settings/support-contacts';
import { WhatsAppIcon } from '@/components/ui/whatsapp-icon';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SubmittedOrder {
  id: string;
  orderNumber?: string | number;
  orderReference?: string;
  branchId?: string;
  customerName?: string;
  customerPhone?: string;
  totalAmount: number;
  items: Array<{ productName: string; quantity: number; unitPrice: number }>;
}

interface BranchConfig {
  mpesa_payment_type: 'buy_goods' | 'stk_push' | 'both';
  mpesa_shortcode: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatOrderLabel(order: SubmittedOrder): string {
  if (order.orderReference) return order.orderReference;
  if (order.orderNumber !== undefined) return `KRB-${String(order.orderNumber).padStart(6, '0')}`;
  return order.id.slice(-8).toUpperCase();
}

function buildWhatsAppMessage(
  order: SubmittedOrder,
  orderLabel: string,
  template?: string,
): string {
  const itemsSummary = order.items
    .map((i) => `${i.quantity}x ${i.productName}`)
    .join(', ');
  const amountSummary = order.totalAmount
    ? ` worth KES ${Number(order.totalAmount).toLocaleString()}`
    : '';

  return renderSmsTemplate(template || WHATSAPP_ORDER_MESSAGE_DEFAULT, {
    order_reference: orderLabel,
    items_summary: itemsSummary || 'my items',
    amount_summary: amountSummary,
    customer_name: order.customerName || '',
  });
}

// ===========================================================================
// Page Component
// ===========================================================================

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preferredAction = searchParams.get('action'); // 'whatsapp' | 'call' | 'sms' | null
  const { items, branchId, updatedAt, clearCart } = useCartStore();
  const [resolvedBranchId, setResolvedBranchId] = useState<string | null>(branchId || null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: '',
    deliveryNotes: '',
  });
  const { settings } = useSettings();
  const pricingConfig = resolvePricingConfig(settings);
  const showPrices = useShowPrices();

  // Post-order state – when set, we show the confirmation/payment panel
  const [submittedOrder, setSubmittedOrder] = useState<SubmittedOrder | null>(null);
  const [branchConfig, setBranchConfig] = useState<BranchConfig>({
    mpesa_payment_type: 'buy_goods',
    mpesa_shortcode: '3022071',
  });

  // Support contact picker
  const supportContacts = settings.support_numbers || [];
  const defaultSupportContact =
    supportContacts.find((c) => c.isDefault) || supportContacts[0] || null;
  const [selectedContactId, setSelectedContactId] = useState('');

  useEffect(() => {
    if (!selectedContactId && defaultSupportContact) {
      setSelectedContactId(defaultSupportContact.id);
    }
  }, [defaultSupportContact, selectedContactId]);

  const selectedContact: SupportContact | null =
    supportContacts.find((c) => c.id === selectedContactId) || defaultSupportContact;

  // Resolve branch
  useEffect(() => {
    let active = true;
    (async () => {
      const id = await resolveDefaultBranchId(branchId, null);
      if (active && id && !branchId) setResolvedBranchId(id);
    })();
    return () => { active = false; };
  }, [branchId]);

  // Load M-Pesa config for branch - fetch immediately for the default/selected branch
  useEffect(() => {
    if (!supabase || !resolvedBranchId) return;
    (async () => {
      const { data, error } = (await supabase
        .from('branches')
        .select('mpesa_payment_type, mpesa_shortcode')
        .eq('id', resolvedBranchId)
        .single()) as { data: BranchConfig | null; error: unknown };
      if (!error && data) setBranchConfig(data);
    })();
  }, [resolvedBranchId]);

  // ---------------------------------------------------------------------------
  // Pricing
  // ---------------------------------------------------------------------------

  const subtotal = items.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
  const tax = subtotal * pricingConfig.vatRate;
  const isFreeDelivery = subtotal >= pricingConfig.freeDeliveryThreshold;
  const deliveryFee = isFreeDelivery ? 0 : pricingConfig.baseDeliveryFee;
  const total = subtotal + tax + deliveryFee;

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleCreateOrder = async (actionOverride?: string) => {
    if (isSubmitting) return;
    if (!customerInfo.name || !customerInfo.phone || !customerInfo.address) return;

    if (items.some((item) => !Number.isFinite(item.unitPrice) || item.unitPrice <= 0)) {
      setSubmitError('Some items in your cart are missing prices. Please remove them and try again.');
      return;
    }

    const action = actionOverride || preferredAction || 'whatsapp';
    setIsSubmitting(true);
    setSubmitError(null);

    const nameParts = customerInfo.name.trim().split(/\s+/);
    const firstName = nameParts[0] || customerInfo.name;
    const lastName = nameParts.slice(1).join(' ');
    const activeBranchId = await resolveDefaultBranchId(branchId, resolvedBranchId);

    if (!activeBranchId) {
      setSubmitError('We could not find an active branch for checkout. Please try again.');
      setIsSubmitting(false);
      return;
    }

    const orderInput = {
      customerName: customerInfo.name.trim(),
      phone: customerInfo.phone.trim(),
      cartVersion: updatedAt,
      postOrderAction: (action as any) || 'whatsapp',
      items: items.map((item) => ({
        productId: item.productId,
        productName: item.product.name,
        variantId: item.variantId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      })),
      deliveryMethod: 'delivery' as const,
      deliveryAddress: {
        firstName,
        lastName,
        phone: customerInfo.phone.trim(),
        email: '',
        street: customerInfo.address.trim(),
        city: '',
        state: '',
        postalCode: '',
        country: 'Kenya',
      },
      branchId: activeBranchId,
      paymentMethod: 'mpesa' as const,
      subtotal,
      tax,
      deliveryFee,
      total,
      notes: customerInfo.deliveryNotes.trim() || undefined,
    };

    try {
      const result = await createOrder(orderInput);
      if (result.success && result.orderId) {
        const orderItems = items.map((i) => ({
          productName: i.product.name,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
        }));
        
        const subOrder: SubmittedOrder = {
          id: result.orderId,
          orderNumber: result.orderNumber,
          orderReference: result.orderReference,
          branchId: activeBranchId,
          customerName: customerInfo.name.trim(),
          customerPhone: customerInfo.phone.trim(),
          totalAmount: total,
          items: orderItems,
        };

        // Complete the order internally
        clearCart();
        setSubmittedOrder(subOrder);

        // Immediate redirect based on the chosen action
        const label = formatOrderLabel(subOrder);
        const msg = buildWhatsAppMessage(subOrder, label, settings.whatsapp_order_message_template);
        const target = getContactWhatsappTarget(selectedContact);
        const phone = selectedContact?.phone || '';

        if (action === 'whatsapp' && target) {
          window.location.href = `https://wa.me/${target}?text=${encodeURIComponent(msg)}`;
        } else if (action === 'call' && phone) {
          window.location.href = `tel:${phone}`;
        } else if (action === 'sms' && phone) {
          window.location.href = `sms:${phone}?body=${encodeURIComponent(msg)}`;
        }
        
        // If it's just 'payment', we stay on the page to show M-Pesa section (which is now inline anyway)
        return;
      }

      setSubmitError(result.message || 'Failed to submit order. Please try again.');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Post-order actions
  // ---------------------------------------------------------------------------

  const orderLabel = submittedOrder ? formatOrderLabel(submittedOrder) : '';
  const whatsAppMessage = submittedOrder
    ? buildWhatsAppMessage(
        submittedOrder,
        orderLabel,
        settings.whatsapp_order_message_template,
      )
    : '';
  const selectedWhatsappTarget = getContactWhatsappTarget(selectedContact);
  const selectedPhone = selectedContact?.phone || '';

  const handleCall = () => {
    if (selectedPhone) window.location.href = `tel:${selectedPhone}`;
  };
  const handleSms = () => {
    if (selectedPhone) window.location.href = `sms:${selectedPhone}?body=${encodeURIComponent(whatsAppMessage)}`;
  };
  const handleWhatsApp = () => {
    if (selectedWhatsappTarget) {
      window.open(
        `https://wa.me/${selectedWhatsappTarget}?text=${encodeURIComponent(whatsAppMessage)}`,
        '_blank',
      );
    }
  };

  // ===========================================================================
  // RENDER — Empty cart
  // ===========================================================================

  if (!submittedOrder && items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
            <p className="text-gray-500 mb-4">Add items to your cart before checking out</p>
            <Button onClick={() => navigate('/')}>Browse Products</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===========================================================================
  // RENDER — Post-order (confirmation + M-Pesa + actions)
  // ===========================================================================

  if (submittedOrder) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Order Created!</h1>
              <p className="text-sm text-gray-500">Complete your payment and contact us below.</p>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
          {/* Success banner */}
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-5 flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-lg font-semibold text-emerald-900">
                  Order {orderLabel}
                </p>
                <p className="text-sm text-emerald-700">
                  {submittedOrder.customerName} • {submittedOrder.items.length} item
                  {submittedOrder.items.length !== 1 ? 's' : ''} •{' '}
                  KES {submittedOrder.totalAmount.toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Order items recap */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Order Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {submittedOrder.items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                  <span>{item.quantity}x {item.productName}</span>
                  <span className="font-medium">KES {(item.quantity * item.unitPrice).toLocaleString()}</span>
                </div>
              ))}
              <Separator />
              <div className="flex items-center justify-between rounded-lg bg-brand-50 px-4 py-3">
                <span className="font-medium text-brand-900">Total</span>
                <span className="text-lg font-bold text-brand-900">
                  KES {submittedOrder.totalAmount.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* M-Pesa Payment — inline */}
          <MpesaPaymentSection
            amount={submittedOrder.totalAmount}
            orderId={submittedOrder.id}
            branchId={submittedOrder.branchId}
            paymentType={branchConfig.mpesa_payment_type}
            tillNumber={branchConfig.mpesa_shortcode}
            supportPhone={selectedPhone}
          />

          {/* Support contact picker */}
          {supportContacts.length > 1 && (
            <Card>
              <CardContent className="p-4">
                <Select
                  label="Support contact for follow-up"
                  value={selectedContact?.id || ''}
                  onValueChange={setSelectedContactId}
                  options={supportContacts.map((c) => ({
                    value: c.id,
                    label: `${c.label} – ${getContactDisplayNumber(c)}`,
                  }))}
                />
              </CardContent>
            </Card>
          )}

          {/* Quick actions — WhatsApp / Call / SMS */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-3">
              <Button
                onClick={handleCall}
                disabled={!selectedPhone}
                className="gap-2"
                variant="outline"
              >
                <Phone className="h-4 w-4" />
                Call Store
              </Button>
              <Button
                onClick={handleSms}
                disabled={!selectedPhone}
                variant="outline"
                className="gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                Send SMS
              </Button>
              <Button
                onClick={handleWhatsApp}
                disabled={!selectedWhatsappTarget}
                className="gap-2 bg-[#25D366] text-white hover:bg-[#1fbc57] border-[#25D366]"
              >
                <WhatsAppIcon className="h-4 w-4" />
                WhatsApp
              </Button>
            </CardContent>
          </Card>

          {/* Message preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-gray-500">Message preview</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{whatsAppMessage}</p>
            </CardContent>
          </Card>

          {/* Done */}
          <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
            Back to Shopping
          </Button>
        </main>
      </div>
    );
  }

  // ===========================================================================
  // RENDER — Checkout form (pre-order)
  // ===========================================================================

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/cart')}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Checkout</h1>
              <p className="text-sm text-gray-500">
                Fill in your details and we'll create your order.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Redirect Overlay */}
        {isSubmitting && (
          <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-[100] flex flex-col items-center justify-center p-6 text-center">
            <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mb-4" />
            <h2 className="text-xl font-bold text-brand-900">Creating your order...</h2>
            <p className="text-brand-600 mt-2">Redirecting you to the selected contact method.</p>
          </div>
        )}

        {/* Order summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Order Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="flex justify-between items-center">
                <div>
                  <p className="font-medium">{item.product.name}</p>
                  <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                </div>
                <p className="font-medium">KES {(item.unitPrice * item.quantity).toLocaleString()}</p>
              </div>
            ))}
            <Separator />
            {showPrices ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span>KES {subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Delivery Fee</span>
                  <span>KES {deliveryFee.toLocaleString()}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-lg">KES {total.toLocaleString()}</span>
                </div>
              </>
            ) : (
              <div className="text-center py-2">
                <span className="text-gray-500 italic">Contact for pricing</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delivery details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Delivery Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Full Name</label>
                <Input
                  placeholder="Enter your full name"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    className="pl-10"
                    placeholder="e.g., 0712 345678"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Delivery Address
              </label>
              <Input
                placeholder="Enter your delivery address"
                value={customerInfo.address}
                onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Delivery Notes (Optional)</label>
              <Input
                placeholder="e.g., Call when you arrive"
                value={customerInfo.deliveryNotes}
                onChange={(e) => setCustomerInfo({ ...customerInfo, deliveryNotes: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Support contact picker if multiple exist */}
        {supportContacts.length > 1 && (
          <Card>
            <CardContent className="p-4">
              <Select
                label="Selected Branch/Support"
                value={selectedContact?.id || ''}
                onValueChange={setSelectedContactId}
                options={supportContacts.map((c) => ({
                    value: c.id,
                    label: `${c.label} – ${getContactDisplayNumber(c)}`,
                }))}
              />
            </CardContent>
          </Card>
        )}

        {/* Action Buttons — Direct Redirects */}
        <div className="grid gap-3">
          <Button
            className="w-full h-14 text-lg font-bold gap-2 bg-[#25D366] text-white hover:bg-[#1fbc57]"
            onClick={() => handleCreateOrder('whatsapp')}
            disabled={!customerInfo.name || !customerInfo.phone || !customerInfo.address || isSubmitting}
          >
            <WhatsAppIcon className="h-6 w-6" />
            Order via WhatsApp
          </Button>
          
          <div className="grid grid-cols-2 gap-3">
            <Button
              className="gap-2 h-12"
              variant="outline"
              onClick={() => handleCreateOrder('call')}
              disabled={!customerInfo.name || !customerInfo.phone || !customerInfo.address || isSubmitting || !selectedPhone}
            >
              <Phone className="h-4 w-4" />
              Order via Call
            </Button>
            <Button
              className="gap-2 h-12"
              variant="outline"
              onClick={() => handleCreateOrder('sms')}
              disabled={!customerInfo.name || !customerInfo.phone || !customerInfo.address || isSubmitting || !selectedPhone}
            >
              <MessageSquare className="h-4 w-4" />
              Order via SMS
            </Button>
          </div>
        </div>

        {submitError && <p className="text-sm text-red-600 text-center font-medium">{submitError}</p>}
        
        <p className="text-xs text-gray-400 text-center px-4">
          By placing an order, you agree to our terms of service. 
          The order will be created and we will contact you via the selected method.
        </p>
      </main>
    </div>
  );
}
